import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  formatInrAmount as formatCurrency,
  formatIndianNumber as formatNumber,
  formatIndianPercent as formatPercent,
  formatCompactInrAxis as formatCompact,
  formatLitres,
} from "../../../utils/reportFormat";

const CHART_COLORS = {
  sales: "#5D38DB",
  productivity: "#0D9488",
};

const formatHours = (value) =>
  `${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 1,
  }).format(value || 0)} hrs`;

const EmployeeTooltip = ({ active, payload, metricLabel }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const row = payload[0]?.payload;
  if (!row) {
    return null;
  }

  return (
    <div className="owner-chart-tooltip">
      <p className="owner-chart-tooltip__title">{row.name}</p>
      <div className="owner-chart-tooltip__row">
        <span className="owner-chart-tooltip__label">{metricLabel}</span>
        <span className="owner-chart-tooltip__value">
          {metricLabel === "Sales / hour"
            ? formatCurrency(row.value)
            : formatCurrency(row.value)}
        </span>
      </div>
      <div className="owner-chart-tooltip__row">
        <span className="owner-chart-tooltip__label">Total sales</span>
        <span className="owner-chart-tooltip__value">
          {formatCurrency(row.total_sales)}
        </span>
      </div>
      <div className="owner-chart-tooltip__row">
        <span className="owner-chart-tooltip__label">Hours worked</span>
        <span className="owner-chart-tooltip__value">
          {formatHours(row.hours_worked)}
        </span>
      </div>
      <div className="owner-chart-tooltip__row">
        <span className="owner-chart-tooltip__label">Days worked</span>
        <span className="owner-chart-tooltip__value">{row.days_worked || 0}</span>
      </div>
    </div>
  );
};

const LeaderboardChart = ({ data, color, metricLabel, emptyMessage }) => {
  if (!data.length) {
    return (
      <div className="owner-chart-empty owner-chart-empty--compact">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={Math.max(260, data.length * 46)}>
      <BarChart
        data={data}
        layout="vertical"
        margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
      >
        <CartesianGrid stroke="#E8ECF1" strokeDasharray="3 3" horizontal={false} />
        <XAxis
          type="number"
          tickFormatter={formatCompact}
          tick={{ fill: "#64748b", fontSize: 12 }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          type="category"
          dataKey="name"
          width={130}
          tick={{ fill: "#475569", fontSize: 12 }}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip content={<EmployeeTooltip metricLabel={metricLabel} />} />
        <Bar dataKey="value" radius={[0, 4, 4, 0]}>
          {data.map((entry) => (
            <Cell key={entry.emp_id || entry.name} fill={color} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

const OwnerEmployeePerformanceChart = ({
  topBySales = [],
  topByProductivity = [],
  employees = [],
  summary = {},
  loading = false,
}) => {
  const tableRows = useMemo(
    () =>
      [...employees].sort(
        (a, b) => (b.total_sales || 0) - (a.total_sales || 0)
      ),
    [employees]
  );

  if (loading) {
    return (
      <div className="owner-chart-empty">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (!employees.length) {
    return (
      <div className="owner-chart-empty">
        <i className="fa fa-users owner-chart-empty__icon" aria-hidden="true" />
        <p>No employee performance data for the selected period.</p>
      </div>
    );
  }

  return (
    <div className="owner-sales-chart">
      <div className="owner-sales-kpi row g-3 mb-4">
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--brand">
            <span className="owner-sales-kpi__label">Top performer</span>
            <strong className="owner-sales-kpi__value">
              {summary?.top_performer || "—"}
            </strong>
            <small className="owner-sales-kpi__hint">
              {formatCurrency(summary?.top_performer_sales)}
            </small>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--teal">
            <span className="owner-sales-kpi__label">Most productive</span>
            <strong className="owner-sales-kpi__value">
              {summary?.most_productive || "—"}
            </strong>
            <small className="owner-sales-kpi__hint">
              {formatCurrency(summary?.most_productive_rate)} / hr
            </small>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--blue">
            <span className="owner-sales-kpi__label">Team sales</span>
            <strong className="owner-sales-kpi__value">
              {formatCurrency(summary?.period_total_sales)}
            </strong>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--green">
            <span className="owner-sales-kpi__label">Active attendants</span>
            <strong className="owner-sales-kpi__value">
              {summary?.active_employees || 0}
            </strong>
            <small className="owner-sales-kpi__hint">
              {formatHours(summary?.period_total_hours)} total
            </small>
          </div>
        </div>
      </div>

      <div className="row g-3 mb-4">
        <div className="col-lg-6">
          <div className="owner-chart-panel h-100">
            <p className="owner-chart-panel__subtitle mb-2">Top employees by sales</p>
            <p className="small text-muted mb-3">
              Fuel + non-fuel + credit + digital sales in the period.
            </p>
            <LeaderboardChart
              data={topBySales}
              color={CHART_COLORS.sales}
              metricLabel="Total sales"
              emptyMessage="No sales recorded for attendants in this period."
            />
          </div>
        </div>

        <div className="col-lg-6">
          <div className="owner-chart-panel h-100">
            <p className="owner-chart-panel__subtitle mb-2">Most productive</p>
            <p className="small text-muted mb-3">
              Sales per hour worked (from nozzle closing shift hours).
            </p>
            <LeaderboardChart
              data={topByProductivity}
              color={CHART_COLORS.productivity}
              metricLabel="Sales / hour"
              emptyMessage="Need at least 1 hour worked to rank productivity."
            />
          </div>
        </div>
      </div>

      <div className="owner-employee-table-wrap">
        <p className="owner-chart-panel__subtitle mb-2">Full KPI breakdown</p>
        <div className="table-responsive">
          <table className="table table-sm owner-employee-table mb-0">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Total sales</th>
                <th>Fuel sales</th>
                <th>Hours</th>
                <th>Sales / hr</th>
                <th>Litres / hr</th>
                <th>Days</th>
                <th>Short</th>
                <th>Excess</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row) => (
                <tr key={row.emp_id}>
                  <td>{row.name}</td>
                  <td>{formatCurrency(row.total_sales)}</td>
                  <td>{formatCurrency(row.fuel_sales)}</td>
                  <td>{formatHours(row.hours_worked)}</td>
                  <td>
                    {row.sales_per_hour != null
                      ? formatCurrency(row.sales_per_hour)
                      : "—"}
                  </td>
                  <td>
                    {row.litres_per_hour != null
                      ? `${row.litres_per_hour} L`
                      : "—"}
                  </td>
                  <td>{row.days_worked || 0}</td>
                  <td>{formatCurrency(row.short_amount)}</td>
                  <td>{formatCurrency(row.excess_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OwnerEmployeePerformanceChart;
